import type { CampaignCardData, CampaignDraftInput, SourceRef, VisualOutput } from "@cafe/shared";
import { AlertTriangle, Check, Database } from "lucide-react";
import { createContext, useContext } from "react";
import { errorMessage, TOOL_LABELS, toolNameOf, type ToolPartLike, unwrapOutput } from "../../lib/tool-parts";
import { Spinner } from "../ui/primitives";
import { CampaignCard } from "./CampaignCard";
import { ProposalCard } from "./ProposalCard";
import { SourceList } from "./SourceList";
import { Visual } from "./Visual";

/** Lets tool parts answer approval requests without prop-drilling the chat helpers. */
export const ApprovalContext = createContext<((id: string, approved: boolean) => void) | null>(null);

const VISUAL_TOOLS = new Set(["show_chart", "show_table", "show_kpis"]);

/**
 * Generative UI. The agent decides what to show:
 *  - data tools (MCP) → a compact "data used" chip with its verifiable sources;
 *  - show_chart / show_table / show_kpis → the visual it chose;
 *  - create_campaign_draft → approval card, then the draft card (with the human-only send).
 */
export function ToolPart({ part }: { part: ToolPartLike }) {
  const name = toolNameOf(part);
  if (name === "create_campaign_draft") return <DraftFlow part={part} />;

  if (part.state === "input-streaming" || part.state === "input-available") {
    return (
      <p className="flex items-center gap-2 text-xs text-roast/80">
        <Spinner className="size-3" /> {TOOL_LABELS[name] ?? name}…
      </p>
    );
  }
  if (part.state === "output-error") return <Chip ok={false} text={`${TOOL_LABELS[name] ?? name}: ${part.errorText ?? "error"}`} />;

  const { data, isError } = unwrapOutput(part.output);
  // A failed visual attempt is an internal retry, not news for the owner: it stays in the trace only.
  if (VISUAL_TOOLS.has(name)) return isError || !data ? null : <Visual v={data as unknown as VisualOutput} />;
  if (isError || !data) return <Chip ok={false} text={`${TOOL_LABELS[name] ?? name}: ${errorMessage(data)}`} />;

  const period = data.period as { from: string; to: string } | undefined;
  const sources = (data.sources as SourceRef[] | undefined) ?? [];
  return (
    <div className="rounded-lg bg-latte/40 px-2 py-1 text-xs text-roast">
      <p className="flex items-center gap-1.5 whitespace-nowrap">
        <Database className="size-3" /> {TOOL_LABELS[name] ?? name}
        {period ? ` · ${period.from} a ${period.to}` : ""}
      </p>
      {sources.length > 0 && <SourceList sources={sources} notes={data.notes as string[] | undefined} />}
    </div>
  );
}

function DraftFlow({ part }: { part: ToolPartLike }) {
  const respond = useContext(ApprovalContext);
  const input = part.input as CampaignDraftInput | undefined;
  switch (part.state) {
    case "input-streaming":
    case "input-available":
      return (
        <p className="flex items-center gap-2 text-xs text-roast/80">
          <Spinner className="size-3" /> Preparando propuesta…
        </p>
      );
    case "approval-requested":
      return input ? <ProposalCard input={input} onRespond={respond && part.approval ? (ok) => respond(part.approval!.id, ok) : undefined} responded={null} /> : null;
    case "approval-responded":
      return input ? <ProposalCard input={input} responded={part.approval?.approved ?? null} /> : null;
    case "output-denied":
      return <Chip ok={false} text="No se creó el borrador: rechazaste la propuesta." />;
    case "output-error":
      return <Chip ok={false} text={`No se pudo crear el borrador: ${part.errorText ?? "error"}`} />;
    case "output-available": {
      const { data, isError } = unwrapOutput(part.output);
      if (isError || !data) return <Chip ok={false} text={`No se pudo crear el borrador: ${errorMessage(data)}`} />;
      if (data.ok === false) return <Chip ok={false} text={`Borrador no creado: ${((data.problems as string[]) ?? []).join(" ")}`} />;
      return <CampaignCard initial={data.campaign as CampaignCardData} />;
    }
    default:
      return null;
  }
}

function Chip({ ok, text }: { ok: boolean; text: string }) {
  return (
    <p className={`inline-flex items-start gap-1.5 rounded-lg px-2 py-1 text-xs ${ok ? "bg-latte/50 text-roast" : "bg-red-50 text-red-800"}`}>
      {ok ? <Check className="mt-0.5 size-3 shrink-0" /> : <AlertTriangle className="mt-0.5 size-3 shrink-0" />}
      {text}
    </p>
  );
}
